import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { apiService } from '../../services/api';
import UniversalModal from '../Common/UniversalModal';
import UnsavedChangesPopover from '../Common/UnsavedChangesPopover';
import Toast from '../Common/Toast';
import '../Common/CardActions.css';
import '../Common/PrimaryActionButton.css';
import './DebtorsList.css';
import DebtorForm from './DebtorForm';
import Pagination from '../Common/Pagination';
import OwnerBadge from '../Common/OwnerBadge';
import { formatLocalDate } from '../../utils/dateTime';
import { useAuth } from '../../context/AuthContext';

const DebtorsList = () => {
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = !!user?.is_staff || !!user?.is_superuser || String(user?.role || '').toLowerCase() === 'admin';
  const [debtors, setDebtors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const typeLabels = {
    legal: 'Юридическое лицо',
    entrepreneur: 'Индивидуальный предприниматель',
    individual: 'Физическое лицо'
  };
  
  // Состояние для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: null, // 'create', 'view', 'edit'
    debtor: null
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const userTouchedRef = useRef(false);
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState({
    type: 'legal',
    name: '',
    legal_form: '',
    inn: '',
    kpp: '',
    ogrn: '',
    snils: '',
    address: '',
    email: '',
    phone: '',
    case_number: '',
    arbitration_court: ''
  });
  
  // Состояние для диалога подтверждения
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Удалить',
    cancelText: 'Отмена'
  });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    loadDebtors();
    // Проверяем, нужно ли открыть модальное окно создания должника
    if (location.state?.openCreateModal) {
      handleAddDebtor();
      // Очищаем state, чтобы модальное окно не открывалось повторно при обновлении
      window.history.replaceState({}, document.title);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiService.getUiSettings();
        const v = Number(resp?.data?.table_page_size);
        if ([12, 24, 48, 96].includes(v)) {
          setItemsPerPage(v);
          setCurrentPage(1);
        }
      } catch (_) {
        // ignore
      }
    })();
  }, []);

  const loadDebtors = async () => {
    try {
      setLoading(true);
      setError('');
      const allDebtors = [];
      let page = 1;
      while (true) {
        const response = await apiService.getDebtors(`?page=${page}&page_size=96`);
        const chunk = response.data?.results || response.data || [];
        allDebtors.push(...chunk);
        if (!response.data?.next) break;
        page += 1;
        if (page > 200) break;
      }
      setDebtors(allDebtors);
    } catch (err) {
      console.error('Error loading debtors:', err);
      setError('Ошибка при загрузке должников: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'legal',
      name: '',
      legal_form: '',
      inn: '',
      kpp: '',
      ogrn: '',
      snils: '',
      address: '',
      email: '',
      phone: '',
      case_number: '',
      arbitration_court: '',
      case_act_court: '',
      case_act_date: '',
      case_act_type: 'decision',
      manager_act_date: '',
      manager_act_independent: false,
      manager_act_type: 'decision',
      arbitration_manager: '',
      manager_act_court: ''
    });
    setHasUnsavedChanges(false);
    userTouchedRef.current = false;
  };
  
  const handleCancelModal = () => {
    if (modalState.mode === 'edit' && !hasUnsavedChanges) {
      setModalState({ ...modalState, mode: 'view' });
      setHasUnsavedChanges(false);
      userTouchedRef.current = false;
      return;
    }
    if (!userTouchedRef.current) {
      setModalState({ isOpen: false, mode: null, debtor: null });
      setHasUnsavedChanges(false);
      resetForm();
      return;
    }
    if (hasUnsavedChanges && modalState.mode !== 'view') {
      showConfirmDialog(
        'Несохраненные изменения',
        'У вас есть несохраненные изменения. Вы уверены, что хотите закрыть окно? Все изменения будут потеряны.',
        () => {
          setModalState({ isOpen: false, mode: null, debtor: null });
          setHasUnsavedChanges(false);
          resetForm();
        },
        'Закрыть без сохранения'
      );
    } else {
      setModalState({ isOpen: false, mode: null, debtor: null });
      setHasUnsavedChanges(false);
      resetForm();
    }
  };

  const handleAddDebtor = () => {
    resetForm();
    userTouchedRef.current = false;
    setModalState({ isOpen: true, mode: 'create', debtor: null });
  };

  const handleViewDebtor = (debtor) => {
    setHasUnsavedChanges(false);
    userTouchedRef.current = false;
    const normalizeDate = (value) => formatLocalDate(value || '') || (value || '');
    // Проставляем formData сразу, чтобы карточка не открывалась "пустой" до синхронизации в DebtorForm
    setFormData({
      ...debtor,
      type: debtor?.type || 'legal',
      procedure: debtor?.procedure
        || (debtor?.type === 'legal' ? 'competition' : 'citizen_property_realization'),
      case_act_date: normalizeDate(debtor?.case_act_date),
      case_act_type: debtor?.case_act_type || 'decision',
      case_act_court: debtor?.case_act_court || debtor?.arbitration_court || '',
      manager_act_independent: !!debtor?.manager_act_independent,
      manager_act_type: debtor?.manager_act_independent
        ? (debtor?.manager_act_type || debtor?.case_act_type || 'decision')
        : (debtor?.case_act_type || debtor?.manager_act_type || 'decision'),
      manager_act_date: debtor?.manager_act_independent
        ? normalizeDate(debtor?.manager_act_date)
        : (normalizeDate(debtor?.manager_act_date) || normalizeDate(debtor?.case_act_date)),
      arbitration_manager: debtor?.arbitration_manager || '',
      manager_act_court: debtor?.manager_act_court || debtor?.arbitration_court || ''
    });
    setModalState({ isOpen: true, mode: 'view', debtor });
  };

  const handleEditDebtor = (debtor) => {
    setHasUnsavedChanges(false);
    userTouchedRef.current = false;
    const normalizeDate = (value) => formatLocalDate(value || '') || (value || '');
    // Проставляем formData сразу, чтобы карточка не открывалась "пустой" до синхронизации в DebtorForm
    setFormData({
      ...debtor,
      type: debtor?.type || 'legal',
      procedure: debtor?.procedure
        || (debtor?.type === 'legal' ? 'competition' : 'citizen_property_realization'),
      case_act_date: normalizeDate(debtor?.case_act_date),
      case_act_type: debtor?.case_act_type || 'decision',
      case_act_court: debtor?.case_act_court || debtor?.arbitration_court || '',
      manager_act_independent: !!debtor?.manager_act_independent,
      manager_act_type: debtor?.manager_act_independent
        ? (debtor?.manager_act_type || debtor?.case_act_type || 'decision')
        : (debtor?.case_act_type || debtor?.manager_act_type || 'decision'),
      manager_act_date: debtor?.manager_act_independent
        ? normalizeDate(debtor?.manager_act_date)
        : (normalizeDate(debtor?.manager_act_date) || normalizeDate(debtor?.case_act_date)),
      arbitration_manager: debtor?.arbitration_manager || '',
      manager_act_court: debtor?.manager_act_court || debtor?.arbitration_court || ''
    });
    setModalState({ isOpen: true, mode: 'edit', debtor });
  };
  
  const handleStartEdit = () => {
    setHasUnsavedChanges(false);
    userTouchedRef.current = false;
    setModalState({ ...modalState, mode: 'edit' });
  };

  useEffect(() => {
    if (!modalState.isOpen || modalState.mode !== 'edit') return;
    const handleEsc = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      handleCancelModal();
    };
    document.addEventListener('keydown', handleEsc, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleEsc, { capture: true });
    };
  }, [modalState.isOpen, modalState.mode, handleCancelModal]);

  const handleDeleteDebtor = (debtor) => {
    showConfirmDialog(
      'Внимание: каскадное удаление',
      `Вы уверены, что хотите удалить должника ${debtor.name}?\n\nВажно: вместе с должником будут удалены карточки торгов, в которых он указан.\nЭто действие нельзя отменить.`,
      async () => {
        try {
          await apiService.deleteDebtor(debtor.id);
          setDebtors(debtors.filter(d => d.id !== debtor.id));
        } catch (err) {
          setError('Ошибка при удалении должника: ' + (err.response?.data?.detail || err.message));
        }
      }
    );
  };

  const handleSaveDebtor = async (payload, { exit } = {}) => {
    try {
      let savedDebtor = null;

      if (modalState.debtor) {
        const response = await apiService.updateDebtor(modalState.debtor.id, payload);
        savedDebtor = response.data || { ...modalState.debtor, ...payload };
        setDebtors(debtors.map(d => d.id === savedDebtor.id ? savedDebtor : d));
      } else {
        const response = await apiService.createDebtor(payload);
        savedDebtor = response.data;
      setDebtors([savedDebtor, ...debtors]);
      }

      setHasUnsavedChanges(false);

      showToast(modalState.debtor ? 'Изменения сохранены' : 'Должник создан');

      if (exit) {
        setModalState({ isOpen: false, mode: null, debtor: null });
        resetForm();
      } else {
        setModalState({ isOpen: true, mode: 'edit', debtor: savedDebtor });
        setFormData({
          ...savedDebtor,
          type: savedDebtor?.type || 'legal',
          procedure: savedDebtor?.procedure
            || (savedDebtor?.type === 'legal' ? 'competition' : 'citizen_property_realization'),
          case_act_date: savedDebtor?.case_act_date || '',
          case_act_type: savedDebtor?.case_act_type || 'decision',
          case_act_court: savedDebtor?.case_act_court || savedDebtor?.arbitration_court || '',
          manager_act_independent: !!savedDebtor?.manager_act_independent,
          manager_act_type: savedDebtor?.manager_act_independent
            ? (savedDebtor?.manager_act_type || savedDebtor?.case_act_type || 'decision')
            : (savedDebtor?.case_act_type || savedDebtor?.manager_act_type || 'decision'),
          manager_act_date: savedDebtor?.manager_act_independent
            ? (savedDebtor?.manager_act_date || '')
            : (savedDebtor?.manager_act_date || savedDebtor?.case_act_date || ''),
          arbitration_manager: savedDebtor?.arbitration_manager || '',
          manager_act_court: savedDebtor?.manager_act_court || savedDebtor?.arbitration_court || ''
        });
      }

      return savedDebtor;
    } catch (err) {
      console.error('Error saving debtor:', err.response?.data || err);
      let errorMessage = 'Ошибка при сохранении должника: ';

      if (err.response?.data) {
        const data = err.response.data;
        if (typeof data === 'string') {
          errorMessage += data;
        } else if (data.detail) {
          errorMessage += data.detail;
        } else if (data.inn) {
          errorMessage += Array.isArray(data.inn) ? data.inn.join(', ') : data.inn;
        } else if (data.name) {
          if (Array.isArray(data.name)) {
            errorMessage += data.name.join(', ');
          } else {
            errorMessage += data.name;
          }
        } else {
          errorMessage += JSON.stringify(data);
        }
      } else {
        errorMessage += err.message;
      }

      setError(errorMessage);
      showToast(errorMessage, 'error');
      return false;
    }
  };

  // Функции для управления диалогом подтверждения
  const showConfirmDialog = (title, message, onConfirm, confirmText = 'Удалить') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      confirmText,
      cancelText: 'Отмена'
    });
  };

  const hideConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  };

  const handleConfirmAction = () => {
    if (confirmDialog.onConfirm) {
      confirmDialog.onConfirm();
    }
    hideConfirmDialog();
  };

  // Обработчики пагинации
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении количества элементов
    apiService.saveUiSettings({ table_page_size: newItemsPerPage }).catch(() => {});
  };

  const filtered = React.useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return debtors;
    return (debtors || []).filter((d) => {
      const hay = [
        d.name,
        d.inn,
        d.ogrn,
        d.snils,
        d.address,
        d.phone,
        d.email,
        d.case_number,
        d.arbitration_court
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [debtors, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, itemsPerPage, debtors]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const duplicateInnMeta = React.useMemo(() => {
    const byInn = new Map();
    (debtors || []).forEach((debtor) => {
      const inn = String(debtor?.inn || '').trim();
      if (!inn) return;
      if (!byInn.has(inn)) {
        byInn.set(inn, { owners: new Set(), count: 0 });
      }
      const meta = byInn.get(inn);
      meta.count += 1;
      const ownerKey = String(debtor?.owner_email || '').trim() || `no-owner-${debtor?.id}`;
      meta.owners.add(ownerKey);
    });
    return byInn;
  }, [debtors]);
  const paged = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filtered.slice(startIndex, startIndex + itemsPerPage);
  }, [filtered, currentPage, itemsPerPage]);

  if (loading) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <h2>Загрузка должников...</h2>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>Должники</h1>
        <button 
          onClick={handleAddDebtor}
          className="primary-create-button"
        >
          <span className="primary-create-button__icon">+</span>
          Добавить должника
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по должникам, ИНН, ОГРН, адресу..."
          style={{ flex: 1, minWidth: 260, padding: 10, border: '1px solid #dee2e6', borderRadius: 10 }}
        />
        <div style={{ color: '#6c757d', whiteSpace: 'nowrap' }}>
          Найдено: <b>{totalItems}</b>
        </div>
      </div>

      {error && (
        <div style={{ 
          backgroundColor: '#fee', 
          border: '1px solid #fcc', 
          color: '#a00', 
          padding: '15px', 
          borderRadius: '4px', 
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{error}</span>
          <button 
            onClick={loadDebtors}
            style={{ 
              padding: '5px 10px', 
              backgroundColor: '#dc3545', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer' 
            }}
          >
            Повторить
          </button>
        </div>
      )}

      <div>
        {paged.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '50px', 
            color: '#666',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}>
            <h3>Должники не найдены</h3>
            <p>Добавьте первого должника через кнопку "Добавить должника"</p>
          </div>
        ) : (
          <div className="debtors-grid">
            {paged.map((debtor) => (
              <div 
                key={debtor.id} 
                className="debtor-card clickable"
                onClick={() => handleViewDebtor(debtor)}
              >
                <div className="debtor-header">
                  <div className="debtor-title-block">
                    <h3 className="debtor-title">{debtor.name}</h3>
                    <OwnerBadge email={debtor.owner_email} />
                  </div>
                  <div className="card-actions">
                    <button
                      type="button"
                      className="card-action-btn card-action-btn--edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditDebtor(debtor);
                      }}
                      title="Редактировать"
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className="card-action-btn card-action-btn--delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDebtor(debtor);
                      }}
                      title="Удалить"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                
                <div className="debtor-info">
                  <div className="info-row">
                    <span className="label">
                      Тип:
                    </span>
                    <span className="value">
                      {typeLabels[debtor.type] || 'Неизвестно'}
                    </span>
                  </div>
                  
                  {debtor.inn && (
                    <div className="info-row">
                      <span className="label">ИНН:</span>
                      <span className="value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{debtor.inn}</span>
                        {(() => {
                          if (!isAdmin) return null;
                          const meta = duplicateInnMeta.get(String(debtor.inn).trim());
                          if (!meta || meta.owners.size < 2) return null;
                          return (
                            <span
                              title={`ИНН используется у ${meta.owners.size} пользователей, записей: ${meta.count}`}
                              style={{
                                fontSize: 11,
                                color: '#8a5a00',
                                background: '#fff3cd',
                                border: '1px solid #ffe69c',
                                borderRadius: 999,
                                padding: '1px 7px',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Дубль у пользователей: {meta.owners.size}
                            </span>
                          );
                        })()}
                      </span>
                    </div>
                  )}

                  {debtor.snils && debtor.type !== 'legal' && (
                    <div className="info-row">
                      <span className="label">СНИЛС:</span>
                      <span className="value">{debtor.snils}</span>
                    </div>
                  )}
                  
                  {debtor.address && (
                    <div className="info-row">
                      <span className="label">Адрес:</span>
                      <span className="value">{debtor.address}</span>
                    </div>
                  )}
                  
                  {debtor.case_number && (
                    <div className="info-row">
                      <span className="label">Дело:</span>
                      <span className="value">{debtor.case_number}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Пагинация */}
        {totalItems > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}
      </div>

      {/* Модальное окно для работы с должниками */}
      <UniversalModal
        isOpen={modalState.isOpen}
        onClose={handleCancelModal}
        title={
          modalState.debtor 
            ? (modalState.mode === 'view' ? `Должник: ${modalState.debtor.name}` : 'Редактирование должника')
            : 'Создание должника'
        }
        isViewMode={modalState.mode === 'view'}
        onEdit={handleStartEdit}
        hasChanges={false}
        disableEscClose={modalState.mode === 'edit' || confirmDialog.isOpen}
        modalClassName="universal-modal--wide"
      >
        <div
          onInputCapture={() => {
            userTouchedRef.current = true;
          }}
          onChangeCapture={() => {
            userTouchedRef.current = true;
          }}
        >
          <DebtorForm
            debtor={modalState.debtor}
            formData={formData}
            setFormData={setFormData}
            onSave={handleSaveDebtor}
            onCancel={handleCancelModal}
            isViewMode={modalState.mode === 'view'}
            showToast={showToast}
            onFormChange={(changed) => {
              if (!userTouchedRef.current && changed) {
                return;
              }
              setHasUnsavedChanges(changed);
            }}
          />
        </div>
      </UniversalModal>

      {/* Диалог подтверждения */}
      {confirmDialog.isOpen && (
        <ConfirmDialog 
          confirmDialog={confirmDialog}
          onCancel={hideConfirmDialog}
          onConfirm={handleConfirmAction}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
};

// Компонент диалога подтверждения с обработчиком Escape
const ConfirmDialog = ({ confirmDialog, onCancel, onConfirm }) => {
  
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation(); 
        e.stopImmediatePropagation();
        onCancel();
        return false;
      }
    };

    document.addEventListener('keydown', handleEscape, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: true });
    };
  }, [onCancel]);

  return (
    <UnsavedChangesPopover
      isOpen={!!confirmDialog?.isOpen}
      title={confirmDialog?.title || 'Подтверждение'}
      message={confirmDialog?.message}
      onCancel={onCancel}
      onConfirm={onConfirm}
      cancelText={confirmDialog?.cancelText || 'Отмена'}
      confirmText={confirmDialog?.confirmText || 'Подтвердить'}
      zIndex={10000}
    />
  );
};

export default DebtorsList;
